import * as THREE from "three";
import type { RouteNode } from "../world/nav";
import type { NavWorld } from "../world/navmesh";
import { bus } from "../core/bus";
import { SoldierInstance, type SoldierAsset } from "./soldier-glb";
import { buildSoldier, animateSoldier, type SoldierRig } from "./soldier-geometry";
import { terrainHeightAt } from "../world/terrain";

const CENTER_SPEED = 5.25;
const MEMBER_SPEED = 6.5;
const IFF_CONE_DOT = 0.985;
// 3 HP per member (12 squad total) gives the player a realistic window to
// break up an assault before the squad wipes; 2 HP made dense holds a
// ~5-second death spiral.
const MEMBER_HP = 3;
// Formation disorder (max member distance from its formation slot, XZ) above
// which the squad center slows to let stragglers reform. Measured against
// slots, not raw distance from center, so steady-state formation depth never
// counts as disorder.
const REGROUP_DISTANCE = 9;
// Tactical holdUntilClear gives up after this long with threats still in the
// hold radius — the squad fights through rather than stalling forever against
// continuous reinforcements.
const HOLD_UNTIL_CLEAR_TIMEOUT = 45;
// How quickly the formation basis turns toward the next route leg (1/s).
// Smoothing prevents the whole formation snapping sideways at waypoint corners.
const FORWARD_TURN_RATE = 2.5;
const SEPARATION_DISTANCE = 3.25;
const FORMATION_LATERAL_MIN = 3.25;
const FORMATION_LATERAL_MAX = 7.5;
const FORMATION_DEPTH_MIN = 3.75;
const FORMATION_DEPTH_MAX = 12.5;
const DEFAULT_TACTICAL_HOLD_RADIUS = 260;

export type TacticalHoldQuery = (node: RouteNode) => boolean;

type LegacyMember = {
  group: THREE.Group;
  rig: SoldierRig;
  slotOffset: THREE.Vector3;
  alive: boolean;
  hp: number;
  name?: string;
  moving: boolean;
  phase: number;
  glb: false;
};

type GlbMember = {
  group: THREE.Group;
  soldier: SoldierInstance;
  slotOffset: THREE.Vector3;
  alive: boolean;
  hp: number;
  name?: string;
  moving: boolean;
  glb: true;
};

type SquadMember = LegacyMember | GlbMember;

const _toWaypoint = new THREE.Vector3();
const _toMember = new THREE.Vector3();
const _forward = new THREE.Vector3();
const _right = new THREE.Vector3();
const _goal = new THREE.Vector3();
const _goalDelta = new THREE.Vector3();
const _separation = new THREE.Vector3();
const _otherDelta = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);

const SQUAD_EMISSIVE = new THREE.Color(0x1133cc);
const DEFAULT_FORMATION_SLOTS: [number, number][] = [
  [-1.0, -0.2],
  [1.0, -0.2],
  [-1.2, -1.0],
  [1.2, -1.0],
  [0, -1.8],
  [-1.6, -1.8],
  [1.6, -1.8],
];

export class Squad {
  readonly group = new THREE.Group();
  private readonly members: SquadMember[] = [];
  private readonly center = new THREE.Vector3();
  private readonly smoothedForward = new THREE.Vector3(0, 0, 1);
  private currentWaypointIdx = 0;
  private elapsed = 0;
  private holdTimer = 0;
  private holdElapsed = 0;
  private holdingWaypointIdx = -1;
  // Navmesh routing — null until the engine finishes the async build, and the
  // squad steers in straight lines until then (progressive enhancement).
  private nav: NavWorld | null = null;
  private centerPath: THREE.Vector3[] | null = null;
  private centerPathIdx = 0;
  // Player Stop/Go command: while true, the squad halts in place (members
  // settle into formation slots) and never advances waypoints. Independent
  // of tactical holds — those still time out on their own.
  private playerHold = false;
  completed = false;

  constructor(
    private readonly waypoints: RouteNode[],
    count = 4,
    private readonly mode: "advance" | "hold" = "advance",
    asset?: SoldierAsset,
    // Incoming-damage multiplier (< 1 = armor field mods). Damage is
    // fractional against integer MEMBER_HP, so 0.75 means every 4th hit is
    // shrugged off across the team.
    private readonly damageTakenMult = 1,
    // Operator names, index-aligned with members (run roster).
    names?: string[],
    // COMBAT MEDIC trait: lethal hits negated this many times per mission.
    private deathPreventions = 0,
  ) {
    if (waypoints.length === 0) {
      throw new Error("Squad requires at least one route node");
    }

    const startWp = waypoints[0]!;
    this.center.copy(startWp.position);
    this.smoothedForward.copy(this.desiredFormationForward());

    for (let i = 0; i < count; i++) {
      const slotTemplate = DEFAULT_FORMATION_SLOTS[i % DEFAULT_FORMATION_SLOTS.length]!;
      const slotOffset = new THREE.Vector3(
        slotTemplate[0] + (Math.random() - 0.5) * 0.18,
        0,
        slotTemplate[1] + (Math.random() - 0.5) * 0.12,
      );
      const spawnPos = this.memberGoal(startWp, slotOffset);
      spawnPos.y = terrainHeightAt(spawnPos.x, spawnPos.z);

      if (asset) {
        const soldier = new SoldierInstance(asset, SQUAD_EMISSIVE, { isSquad: true }, 1.0);
        soldier.group.position.copy(spawnPos);
        soldier.group.traverse((obj) => {
          if (obj instanceof THREE.Mesh || obj instanceof THREE.SkinnedMesh) {
            obj.castShadow = true;
            obj.receiveShadow = true;
          }
        });
        this.group.add(soldier.group);
        const member: GlbMember = { group: soldier.group, soldier, slotOffset, alive: true, hp: MEMBER_HP, moving: false, glb: true };
        if (names?.[i]) member.name = names[i];
        this.members.push(member);
      } else {
        const mat = new THREE.MeshStandardMaterial({ color: 0x2255ff, emissive: new THREE.Color(0x1133cc), emissiveIntensity: 2.0 });
        const rig = buildSoldier(mat, { isSquad: true });
        rig.group.position.copy(spawnPos);
        rig.group.traverse((obj) => {
          if (obj instanceof THREE.Mesh) {
            obj.castShadow = true;
            obj.receiveShadow = true;
          }
        });
        this.group.add(rig.group);
        const member: LegacyMember = {
          group: rig.group,
          rig,
          slotOffset,
          alive: true,
          hp: MEMBER_HP,
          moving: false,
          phase: Math.random() * Math.PI * 2,
          glb: false,
        };
        if (names?.[i]) member.name = names[i];
        this.members.push(member);
      }
    }
  }

  update(dt: number, hasBlockingThreat?: TacticalHoldQuery): void {
    if (this.completed) return;
    this.elapsed += dt;

    const wp = this.waypoints[this.currentWaypointIdx];
    if (!wp) return;

    const alive = this.members.filter((m) => m.alive);
    if (alive.length === 0) return;

    this.updateFormationForward(dt);

    if (this.mode === "advance" && !this.playerHold) {
      if (this.holdingWaypointIdx === this.currentWaypointIdx) {
        this.updateHold(dt, wp, hasBlockingThreat);
      } else {
        this.updateCenter(dt, wp);
      }
    }

    this.updateMembers(dt, wp, alive);

    if (this.mode !== "advance" || this.completed || this.playerHold) return;
    if (this.holdingWaypointIdx === this.currentWaypointIdx) return;

    if (this.isWaypointSecured(wp, alive)) {
      bus.emit("squad:waypointReached", { index: this.currentWaypointIdx });
      if (this.shouldHoldAt(wp, hasBlockingThreat)) {
        this.holdingWaypointIdx = this.currentWaypointIdx;
        this.holdTimer = wp.holdTime ?? 0;
        this.holdElapsed = 0;
      } else {
        this.finishWaypoint();
      }
    }
  }

  // Current tactical hold zone, or null when the squad is moving.
  // Used to keep ambient reinforcements from repopulating a zone the squad
  // is actively waiting on.
  activeHold(): { position: THREE.Vector3; radius: number } | null {
    if (this.completed || this.holdingWaypointIdx !== this.currentWaypointIdx) return null;
    const node = this.waypoints[this.currentWaypointIdx];
    if (!node?.holdUntilClear) return null;
    return { position: node.position, radius: Squad.tacticalHoldRadius(node) };
  }

  checkIFF(camPos: THREE.Vector3, camFwd: THREE.Vector3): boolean {
    for (const m of this.members) {
      if (!m.alive) continue;
      _toMember.copy(m.group.position).sub(camPos).normalize();
      if (_toMember.dot(camFwd) > IFF_CONE_DOT) return true;
    }
    return false;
  }

  get aliveCount(): number { return this.members.filter((m) => m.alive).length; }
  get totalCount(): number { return this.members.length; }
  get currentObjectiveLabel(): string { return this.completed ? "COMPLETE" : this.waypoints[this.currentWaypointIdx]?.label ?? "COMPLETE"; }
  get currentWaypointIndex(): number { return this.currentWaypointIdx; }

  memberPositions(): THREE.Vector3[] {
    return this.members.filter((m) => m.alive).map((m) => m.group.position.clone());
  }

  takeDamage(): void {
    const alive = this.members.filter((m) => m.alive);
    if (alive.length === 0) return;
    const target = alive[Math.floor(Math.random() * alive.length)]!;
    this.damageMember(target, this.damageTakenMult, false);
  }

  // Player ordnance landing on the squad. Returns members hit (0 = clean).
  // Blast damage ignores plates — a 105 is a 105 (docs/design/GDD.md:
  // "Highest friendly fire risk. Use with care near the squad.").
  hitInRadius(center: THREE.Vector3, radius: number): number {
    let hits = 0;
    for (const m of this.members) {
      if (!m.alive) continue;
      const dx = m.group.position.x - center.x;
      const dz = m.group.position.z - center.z;
      if (Math.hypot(dx, dz) > radius) continue;
      hits++;
      this.damageMember(m, 1, true);
    }
    return hits;
  }

  private damageMember(target: SquadMember, amount: number, friendlyFire: boolean): void {
    target.hp -= amount;
    if (target.hp > 0) return;
    // Combat medic: the first lethal hit(s) get someone back on their feet
    if (this.deathPreventions > 0) {
      this.deathPreventions--;
      target.hp = 1;
      bus.emit("squad:deathPrevented", { name: target.name ?? "operator" });
      return;
    }
    target.alive = false;
    if (target.glb) {
      target.soldier.die();
    } else {
      target.group.rotation.z = Math.PI / 2;
      target.group.position.y = terrainHeightAt(target.group.position.x, target.group.position.z) + 0.3;
    }
    const idx = this.members.indexOf(target);
    const event: { index: number; name?: string; friendlyFire?: boolean } = { index: idx, friendlyFire };
    if (target.name) event.name = target.name;
    bus.emit("squad:memberDown", event);
  }

  // Names of members still standing (undefined entries filtered) — the run
  // roster carries these forward.
  survivorNames(): string[] {
    return this.members
      .filter((m) => m.alive && m.name)
      .map((m) => m.name!);
  }

  setNavWorld(nav: NavWorld | null): void {
    this.nav = nav;
    this.centerPath = null;
  }

  // Player Stop/Go command. Returns the new state.
  togglePlayerHold(): boolean {
    this.playerHold = !this.playerHold;
    if (!this.playerHold) this.centerPath = null; // re-path from wherever we stopped
    bus.emit("squad:commandHold", { holding: this.playerHold });
    return this.playerHold;
  }

  get isPlayerHolding(): boolean {
    return this.playerHold;
  }

  private updateCenter(dt: number, node: RouteNode): void {
    if (this.center.distanceTo(node.position) <= node.arrivalRadius * 0.35) return;

    if (this.nav && !this.centerPath) {
      this.centerPath = this.nav.findPath(this.center, node.position);
      this.centerPathIdx = 0;
    }

    // Steer toward the next path corner when a navmesh route exists;
    // otherwise straight at the waypoint (pre-nav behavior).
    const corner = this.centerPath?.[Math.min(this.centerPathIdx, this.centerPath.length - 1)] ?? node.position;
    _toWaypoint.copy(corner).sub(this.center);
    _toWaypoint.y = 0; // center is planar; path corners carry navmesh height
    const distance = _toWaypoint.length();

    if (this.centerPath && distance <= 2.0 && this.centerPathIdx < this.centerPath.length - 1) {
      this.centerPathIdx++;
      return;
    }
    if (distance < 0.001) return;

    let speed = CENTER_SPEED;
    const disorder = this.formationDisorder();
    if (disorder > REGROUP_DISTANCE) {
      const penalty = THREE.MathUtils.clamp((disorder - REGROUP_DISTANCE) / REGROUP_DISTANCE, 0, 0.75);
      speed *= 1 - penalty;
    }

    if (speed <= 0.25) return;
    this.center.addScaledVector(_toWaypoint.normalize(), Math.min(distance, speed * dt));
  }

  private updateMembers(dt: number, node: RouteNode, alive: SquadMember[]): void {
    for (const member of alive) {
      const goal = this.memberGoal(node, member.slotOffset);
      _goalDelta.copy(goal).sub(member.group.position);
      _goalDelta.y = 0; // members follow terrain height; goals are planar
      const distance = _goalDelta.length();
      const moving = distance > 0.45;

      if (moving) {
        member.moving = true;
        member.group.position.addScaledVector(_goalDelta.normalize(), Math.min(distance, MEMBER_SPEED * dt));
        member.group.rotation.y = Math.atan2(_goalDelta.x, _goalDelta.z);
      } else {
        member.moving = false;
      }

      this.applySeparation(member, alive, dt);
      const bob = member.moving ? Math.sin(this.elapsed * 8 + this.memberPhase(member)) * 0.04 : 0;
      member.group.position.y = terrainHeightAt(member.group.position.x, member.group.position.z) + bob;
      this.updateAnimation(member, dt);
    }
  }

  private updateAnimation(member: SquadMember, dt: number): void {
    if (member.glb) {
      member.soldier.setMoving(member.moving);
      member.soldier.update(dt);
      return;
    }

    member.phase += dt;
    animateSoldier(member.rig, member.phase, member.moving);
  }

  private applySeparation(member: SquadMember, alive: SquadMember[], dt: number): void {
    _separation.set(0, 0, 0);
    for (const other of alive) {
      if (other === member) continue;
      _otherDelta.copy(member.group.position).sub(other.group.position);
      _otherDelta.y = 0;
      const distance = _otherDelta.length();
      if (distance === 0 || distance >= SEPARATION_DISTANCE) continue;
      _separation.addScaledVector(
        _otherDelta.normalize(),
        (SEPARATION_DISTANCE - distance) / SEPARATION_DISTANCE,
      );
    }

    if (_separation.lengthSq() === 0) return;
    member.group.position.addScaledVector(_separation.normalize(), dt * 1.5);
  }

  private isWaypointSecured(node: RouteNode, alive: SquadMember[]): boolean {
    if (this.center.distanceTo(node.position) > node.arrivalRadius) return false;
    return this.formationDisorder(alive) <= this.allowedSpread(node);
  }

  private finishWaypoint(): void {
    if (this.currentWaypointIdx >= this.waypoints.length - 1) {
      this.completed = true;
      this.holdingWaypointIdx = -1;
      this.holdTimer = 0;
      this.holdElapsed = 0;
      bus.emit("squad:completed");
      return;
    }

    this.currentWaypointIdx++;
    this.holdingWaypointIdx = -1;
    this.holdTimer = 0;
    this.holdElapsed = 0;
    this.centerPath = null; // route to the new waypoint on the next update
  }

  private updateHold(dt: number, node: RouteNode, hasBlockingThreat?: TacticalHoldQuery): void {
    this.holdTimer = Math.max(0, this.holdTimer - dt);
    if (this.holdTimer > 0) return;
    if (this.hasTacticalBlock(node, hasBlockingThreat)) {
      this.holdElapsed += dt;
      if (this.holdElapsed < HOLD_UNTIL_CLEAR_TIMEOUT) return;
      // Threats outlasted the hold window — advance anyway so continuous
      // reinforcement pressure can never deadlock the route.
    }
    this.finishWaypoint();
  }

  private shouldHoldAt(node: RouteNode, hasBlockingThreat?: TacticalHoldQuery): boolean {
    return (node.holdTime ?? 0) > 0 || this.hasTacticalBlock(node, hasBlockingThreat);
  }

  private hasTacticalBlock(node: RouteNode, hasBlockingThreat?: TacticalHoldQuery): boolean {
    return node.holdUntilClear === true && hasBlockingThreat !== undefined && hasBlockingThreat(node);
  }

  // Max XZ distance of any alive member from its formation slot. Structural
  // formation depth contributes zero here, so a squad in good order reads as
  // disorder ≈ 0 regardless of corridor width or member count.
  private formationDisorder(alive = this.members.filter((m) => m.alive)): number {
    const node = this.waypoints[this.currentWaypointIdx] ?? this.waypoints[this.waypoints.length - 1]!;
    let maxDistance = 0;
    for (const member of alive) {
      const goal = this.memberGoal(node, member.slotOffset);
      const dx = member.group.position.x - goal.x;
      const dz = member.group.position.z - goal.z;
      maxDistance = Math.max(maxDistance, Math.hypot(dx, dz));
    }
    return maxDistance;
  }

  private allowedSpread(node: RouteNode): number {
    const corridorWidth = node.corridorWidth ?? 24;
    return Math.max(REGROUP_DISTANCE, corridorWidth * 0.6);
  }

  static tacticalHoldRadius(node: RouteNode): number {
    return node.holdRadius ?? DEFAULT_TACTICAL_HOLD_RADIUS;
  }

  private desiredFormationForward(): THREE.Vector3 {
    const current = this.waypoints[this.currentWaypointIdx];
    const next = this.waypoints[this.currentWaypointIdx + 1];
    const previous = this.waypoints[this.currentWaypointIdx - 1];

    if (next) _forward.copy(next.position).sub(this.center);
    else if (previous && current) _forward.copy(current.position).sub(previous.position);
    else _forward.set(0, 0, 1);

    _forward.y = 0;
    if (_forward.lengthSq() < 0.001) _forward.set(0, 0, 1);
    return _forward.normalize();
  }

  private updateFormationForward(dt: number): void {
    const target = this.desiredFormationForward();
    const alpha = 1 - Math.exp(-FORWARD_TURN_RATE * dt);
    this.smoothedForward.lerp(target, alpha);
    if (this.smoothedForward.lengthSq() < 0.001) this.smoothedForward.copy(target);
    this.smoothedForward.normalize();
  }

  private memberGoal(node: RouteNode, slotOffset: THREE.Vector3): THREE.Vector3 {
    const forward = this.smoothedForward;
    _right.crossVectors(_up, forward).normalize();

    const corridorWidth = node.corridorWidth ?? 24;
    const lateralScale = THREE.MathUtils.clamp(corridorWidth * 0.25, FORMATION_LATERAL_MIN, FORMATION_LATERAL_MAX);
    const depthScale = THREE.MathUtils.clamp(corridorWidth * 0.45, FORMATION_DEPTH_MIN, FORMATION_DEPTH_MAX);

    _goal.copy(this.center)
      .addScaledVector(_right, slotOffset.x * lateralScale)
      .addScaledVector(forward, slotOffset.z * depthScale);
    // Slots that land inside a building snap to the nearest walkable spot.
    return this.nav ? this.nav.clampToNav(_goal, _goal) : _goal;
  }

  private memberPhase(member: SquadMember): number {
    return member.glb ? 0 : member.phase;
  }
}
